import { inferFieldType } from "../utils/firestoreValueUtils";

export function simulateBulkUpdate(rows, request) {
  const changes = [];
  const warnings = [];
  const affectedFields = new Set();

  for (const row of rows) {
    const before = { ...row.data };
    const after = { ...row.data };
    const field = request.field;

    switch (request.type) {
      case "setField":
        after[field] = request.value;
        break;
      case "deleteField":
        delete after[field];
        break;
      case "renameField":
        if (field in after) {
          after[request.newField] = after[field];
          delete after[field];
        }
        break;
      case "addField":
        after[field] = request.value;
        break;
      case "replaceText":
        if (typeof after[field] === "string") {
          after[field] = after[field].split(request.find).join(request.replace);
        }
        break;
      case "uppercase":
        if (typeof after[field] === "string") after[field] = after[field].toUpperCase();
        break;
      case "lowercase":
        if (typeof after[field] === "string") after[field] = after[field].toLowerCase();
        break;
      case "trim":
        if (typeof after[field] === "string") after[field] = after[field].trim();
        break;
      case "numericDelta":
        after[field] = Number(after[field] || 0) + Number(request.numericDelta || 0);
        break;
      default:
        break;
    }

    affectedFields.add(field);
    if (request.newField) affectedFields.add(request.newField);
    changes.push({ id: row.id, before, after });
  }

  return {
    affectedDocuments: rows.length,
    changedFields: [...affectedFields],
    changes: changes.slice(0, 20),
    warnings,
    estimatedWrites: rows.length,
    estimatedSeconds: Math.ceil(rows.length / 400) * 2,
  };
}

export function simulateBulkDelete(rows) {
  return {
    affectedDocuments: rows.length,
    changedFields: ["(document)"],
    changes: rows.slice(0, 20).map((r) => ({ id: r.id, before: r.data, after: null })),
    warnings: ["Documents will be permanently deleted unless restored from backup."],
    estimatedWrites: rows.length,
    estimatedSeconds: Math.ceil(rows.length / 400) * 2,
  };
}

export function buildBulkUpdates(rows, request) {
  return rows.map((row) => {
    const data = { ...row.data };
    const field = request.field;
    switch (request.type) {
      case "setField":
        data[field] = request.value;
        break;
      case "deleteField":
        delete data[field];
        break;
      case "renameField":
        if (field in data) {
          data[request.newField] = data[field];
          delete data[field];
        }
        break;
      case "addField":
        data[field] = request.value;
        break;
      case "replaceText":
        if (typeof data[field] === "string") {
          data[field] = data[field].split(request.find).join(request.replace);
        }
        break;
      case "uppercase":
        if (typeof data[field] === "string") data[field] = data[field].toUpperCase();
        break;
      case "lowercase":
        if (typeof data[field] === "string") data[field] = data[field].toLowerCase();
        break;
      case "trim":
        if (typeof data[field] === "string") data[field] = data[field].trim();
        break;
      case "numericDelta":
        data[field] = Number(data[field] || 0) + Number(request.numericDelta || 0);
        break;
      default:
        break;
    }
    return { id: row.id, data };
  });
}

export function analyzeCollection(rows) {
  const fieldStats = {};
  const duplicates = {};
  let totalBytes = 0;

  for (const row of rows) {
    try {
      totalBytes += new Blob([JSON.stringify(row.data)]).size;
    } catch {
      /* ignore */
    }
    for (const [k, v] of Object.entries(row.data || {})) {
      if (!fieldStats[k]) {
        fieldStats[k] = { count: 0, nulls: 0, types: {} };
      }
      fieldStats[k].count += 1;
      if (v == null) fieldStats[k].nulls += 1;
      const t = inferFieldType(v);
      fieldStats[k].types[t] = (fieldStats[k].types[t] || 0) + 1;
    }
  }

  return {
    documentCount: rows.length,
    estimatedBytes: totalBytes,
    fieldStats,
    duplicates,
  };
}
